import React, { createContext, useContext, useEffect, useState } from "react";

export type CartProduct = {
  id: string;
  name: string;
  description: string;
  price: number;
  image_url: string;
  brandId: number;
  categoryId: number;
  quantity?: number;
  weight: number;
  length: number;
  width: number;
  height: number;
  is_active?: boolean;
  variant_id?: string;
  size_label?: string;
};

export type CartItem = CartProduct & {
  quantity: number;
};

type CartContextType = {
  cart: CartItem[];
  addToCart: (product: CartProduct, quantity: number) => void;
  removeFromCart: (productId: string, variantId?: string) => void;
  clearCart: () => void;
  updateQuantity: (
    productId: string,
    quantity: number,
    variantId?: string
  ) => void;
};

const CartContext = createContext<CartContextType | undefined>(undefined);

const getCartItemKey = (item: { id: string; variant_id?: string }) => {
  return `${item.id}__${item.variant_id || "no-variant"}`;
};

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
  const [cart, setCart] = useState<CartItem[]>(() => {
    const stored = sessionStorage.getItem("cart");
    return stored ? JSON.parse(stored) : [];
  });

  useEffect(() => {
    sessionStorage.setItem("cart", JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product: CartProduct, quantity: number) => {
    setCart((prev) => {
      const productKey = getCartItemKey({
        id: product.id,
        variant_id: product.variant_id,
      });

      const existing = prev.find(
        (item) => getCartItemKey(item) === productKey
      );

      if (existing) {
        return prev.map((item) =>
          getCartItemKey(item) === productKey
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }

      return [
        ...prev,
        {
          ...product,
          quantity,
        },
      ];
    });
  };

  const removeFromCart = (productId: string, variantId?: string) => {
    setCart((prev) =>
      prev.filter(
        (item) =>
          !(
            item.id === productId &&
            (item.variant_id || "") === (variantId || "")
          )
      )
    );
  };

  const updateQuantity = (
    productId: string,
    quantity: number,
    variantId?: string
  ) => {
    setCart((prev) =>
      prev.map((item) =>
        item.id === productId &&
        (item.variant_id || "") === (variantId || "")
          ? { ...item, quantity: Math.max(1, quantity) }
          : item
      )
    );
  };

  const clearCart = () => setCart([]);

  return (
    <CartContext.Provider
      value={{ cart, addToCart, removeFromCart, clearCart, updateQuantity }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within a CartProvider");
  return ctx;
};